import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { OpeningHours, StoreContact, StoreProfileDetail } from '../types';

interface OverviewSectionEditorsProps {
  profile: StoreProfileDetail;
  storeId: number;
  storeIdParam: string;
  editable?: boolean;
}

async function invalidateOverviewQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  storeIdParam: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
    queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
  ]);
}

function CardEditButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="icon"
      aria-label="Edit section"
      className="h-8 w-8 shrink-0 border border-slate-200 bg-slate-100 text-slate-800 shadow-sm hover:bg-slate-200 hover:text-slate-950 [&_svg]:text-slate-800"
      onClick={onClick}
    >
      <Pencil className="h-4 w-4" />
    </Button>
  );
}

export function OverviewSectionEditors({
  profile,
  storeId,
  storeIdParam,
  editable = true,
}: OverviewSectionEditorsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [savingSection, setSavingSection] = useState<string | null>(null);

  const [editingDetails, setEditingDetails] = useState(false);
  const [storeFormatLabel, setStoreFormatLabel] = useState(profile.storeFormat);
  const [salesAreaSqm, setSalesAreaSqm] = useState(String(profile.storeDimensions.salesAreaSqm || ''));
  const [totalFloorAreaSqm, setTotalFloorAreaSqm] = useState(String(profile.storeDimensions.totalFloorAreaSqm || ''));
  const [ceilingHeightMetres, setCeilingHeightMetres] = useState(String(profile.storeDimensions.ceilingHeightMetres || ''));
  const [checkoutCount, setCheckoutCount] = useState(String(profile.storeDimensions.checkoutCount || ''));

  const [editingHours, setEditingHours] = useState(false);
  const [openingHours, setOpeningHours] = useState<OpeningHours[]>(profile.openingHours);

  const [editingContacts, setEditingContacts] = useState(false);
  const [contacts, setContacts] = useState<StoreContact[]>(profile.contacts);

  const [editingAccess, setEditingAccess] = useState(false);
  const [accessNotes, setAccessNotes] = useState(profile.accessNotes);
  const [parkingNotes, setParkingNotes] = useState(profile.parkingNotes);
  const [loadingNotes, setLoadingNotes] = useState(profile.loadingNotes);
  const [deliveryNotes, setDeliveryNotes] = useState(profile.deliveryNotes);

  const savePatch = async (section: string, body: Record<string, unknown>, onSuccess: () => void) => {
    setSavingSection(section);
    try {
      const response = await apiRequest(`/api/store-profiles/${storeId}/overview`, 'PATCH', body);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error ?? 'Failed to save');
      }
      await invalidateOverviewQueries(queryClient, storeIdParam);
      onSuccess();
      toast({ title: 'Saved' });
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSavingSection(null);
    }
  };

  const parseNumber = (value: string) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Store Details</CardTitle>
            {editable && !editingDetails && <CardEditButton onClick={() => setEditingDetails(true)} />}
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {editingDetails ? (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Format</Label>
                  <Input value={storeFormatLabel} onChange={(e) => setStoreFormatLabel(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Sales area (m²)</Label>
                    <Input value={salesAreaSqm} onChange={(e) => setSalesAreaSqm(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Total floor (m²)</Label>
                    <Input value={totalFloorAreaSqm} onChange={(e) => setTotalFloorAreaSqm(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Ceiling height (m)</Label>
                    <Input value={ceilingHeightMetres} onChange={(e) => setCeilingHeightMetres(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Checkouts</Label>
                    <Input value={checkoutCount} onChange={(e) => setCheckoutCount(e.target.value)} />
                  </div>
                </div>
                <p className="text-xs text-gray-500">Aisles ({profile.aisleCount}) come from the floorplan — edit there.</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={savingSection === 'details'}
                    onClick={() => void savePatch('details', {
                      storeFormatLabel: storeFormatLabel.trim() || null,
                      salesAreaSqm: parseNumber(salesAreaSqm),
                      totalFloorAreaSqm: parseNumber(totalFloorAreaSqm),
                      ceilingHeightMetres: parseNumber(ceilingHeightMetres),
                      checkoutCount: parseNumber(checkoutCount) != null ? Math.round(parseNumber(checkoutCount)!) : null,
                    }, () => setEditingDetails(false))}
                  >
                    {savingSection === 'details' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                  <Button size="sm" variant="outline" className="text-gray-700" onClick={() => setEditingDetails(false)}><X className="h-4 w-4" /></Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-between"><span className="text-gray-500">Format</span><span>{profile.storeFormat}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Sales area</span><span>{profile.storeDimensions.salesAreaSqm} m²</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Total floor</span><span>{profile.storeDimensions.totalFloorAreaSqm} m²</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Ceiling height</span><span>{profile.storeDimensions.ceilingHeightMetres}m</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Checkouts</span><span>{profile.storeDimensions.checkoutCount}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Aisles</span><span>{profile.aisleCount}</span></div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Opening Hours</CardTitle>
            {editable && !editingHours && <CardEditButton onClick={() => setEditingHours(true)} />}
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {editingHours ? (
              <div className="space-y-3">
                {openingHours.map((row, index) => (
                  <div key={`${row.day}-${index}`} className="grid grid-cols-4 gap-2 items-end">
                    <div className="col-span-2">
                      <Label className="text-xs">Day</Label>
                      <Input
                        value={row.day}
                        onChange={(e) => setOpeningHours((current) => current.map((item, i) => (
                          i === index ? { ...item, day: e.target.value } : item
                        )))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Open</Label>
                      <Input
                        value={row.open}
                        onChange={(e) => setOpeningHours((current) => current.map((item, i) => (
                          i === index ? { ...item, open: e.target.value } : item
                        )))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Close</Label>
                      <Input
                        value={row.close}
                        onChange={(e) => setOpeningHours((current) => current.map((item, i) => (
                          i === index ? { ...item, close: e.target.value } : item
                        )))}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOpeningHours((current) => [...current, { day: 'New day', open: '09:00', close: '18:00' }])}
                >
                  <Plus className="h-4 w-4 mr-1" />Add row
                </Button>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={savingSection === 'hours'}
                    onClick={() => void savePatch('hours', { openingHours }, () => setEditingHours(false))}
                  >
                    {savingSection === 'hours' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                  <Button size="sm" variant="outline" className="text-gray-700" onClick={() => setEditingHours(false)}><X className="h-4 w-4" /></Button>
                </div>
              </div>
            ) : (
              profile.openingHours.map((h) => (
                <div key={h.day} className="flex justify-between">
                  <span className="text-gray-500">{h.day}</span>
                  <span>{h.open} – {h.close}</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Contacts</CardTitle>
          {editable && !editingContacts && <CardEditButton onClick={() => setEditingContacts(true)} />}
        </CardHeader>
        <CardContent>
          {editingContacts ? (
            <div className="space-y-3">
              {contacts.map((contact, index) => (
                <div key={`${contact.name}-${index}`} className="grid grid-cols-1 md:grid-cols-4 gap-2 border rounded-lg p-3">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={contact.name}
                      onChange={(e) => setContacts((current) => current.map((item, i) => (
                        i === index ? { ...item, name: e.target.value } : item
                      )))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Role</Label>
                    <Input
                      value={contact.role}
                      onChange={(e) => setContacts((current) => current.map((item, i) => (
                        i === index ? { ...item, role: e.target.value } : item
                      )))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input
                      value={contact.phone ?? ''}
                      onChange={(e) => setContacts((current) => current.map((item, i) => (
                        i === index ? { ...item, phone: e.target.value } : item
                      )))}
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Email</Label>
                      <Input
                        value={contact.email ?? ''}
                        onChange={(e) => setContacts((current) => current.map((item, i) => (
                          i === index ? { ...item, email: e.target.value } : item
                        )))}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setContacts((current) => current.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setContacts((current) => [...current, { name: '', role: 'Store Manager', phone: '', email: '' }])}
              >
                <Plus className="h-4 w-4 mr-1" />Add contact
              </Button>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={savingSection === 'contacts'}
                  onClick={() => void savePatch('contacts', {
                    contacts: contacts
                      .map((c) => ({
                        name: c.name.trim(),
                        role: c.role.trim(),
                        phone: c.phone?.trim() || undefined,
                        email: c.email?.trim() || undefined,
                      }))
                      .filter((c) => c.name && c.role),
                  }, () => setEditingContacts(false))}
                >
                  {savingSection === 'contacts' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
                <Button size="sm" variant="outline" className="text-gray-700" onClick={() => setEditingContacts(false)}><X className="h-4 w-4" /></Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {profile.contacts.map((contact) => (
                <div key={contact.name + contact.role} className="border rounded-lg p-3">
                  <p className="font-medium">{contact.name}</p>
                  <p className="text-sm text-gray-500">{contact.role}</p>
                  {contact.phone && <p className="text-sm mt-1">{contact.phone}</p>}
                  {contact.email && <p className="text-sm">{contact.email}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Access, Parking & Delivery</CardTitle>
          {editable && !editingAccess && <CardEditButton onClick={() => setEditingAccess(true)} />}
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {editingAccess ? (
            <div className="space-y-3">
              {([
                ['Access', accessNotes, setAccessNotes],
                ['Parking', parkingNotes, setParkingNotes],
                ['Loading', loadingNotes, setLoadingNotes],
                ['Deliveries', deliveryNotes, setDeliveryNotes],
              ] as const).map(([label, value, setter]) => (
                <div key={label}>
                  <Label className="text-xs">{label}</Label>
                  <Textarea value={value} onChange={(e) => setter(e.target.value)} rows={3} />
                </div>
              ))}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={savingSection === 'access'}
                  onClick={() => void savePatch('access', {
                    accessNotes,
                    parkingNotes,
                    loadingNotes,
                    deliveryNotes,
                  }, () => setEditingAccess(false))}
                >
                  {savingSection === 'access' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
                <Button size="sm" variant="outline" className="text-gray-700" onClick={() => setEditingAccess(false)}><X className="h-4 w-4" /></Button>
              </div>
            </div>
          ) : (
            <>
              <div><span className="font-medium">Access: </span>{profile.accessNotes}</div>
              <div><span className="font-medium">Parking: </span>{profile.parkingNotes}</div>
              <div><span className="font-medium">Loading: </span>{profile.loadingNotes}</div>
              <div><span className="font-medium">Deliveries: </span>{profile.deliveryNotes}</div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
